/**
 * Token Refresh Utilities
 *
 * Provides JWT decoding and token expiry checking functionality
 * for proactive token refresh before expiration.
 */

interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

// Singleton promise to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<boolean> | null = null;

/**
 * Decode a JWT token without verification (client-side only)
 * @param token - The JWT token string (with or without 'Bearer ' prefix)
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // Remove 'Bearer ' prefix if present
    const cleanToken = token.replace(/^Bearer\s+/i, '');

    // JWT structure: header.payload.signature
    const parts = cleanToken.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Handle base64url encoding (replace - with + and _ with /)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Decode and parse
    const decoded = atob(base64);
    return JSON.parse(decoded) as JWTPayload;
  } catch {
    return null;
  }
}

/**
 * Check if a token is expired or expiring soon
 * @param token - The JWT token string
 * @param bufferSeconds - Seconds before actual expiry to consider as "expiring" (default: 120)
 * @returns true if token is expired or expiring within buffer time
 */
export function isTokenExpiring(
  token: string,
  bufferSeconds: number = 120
): boolean {
  const payload = decodeJWT(token);

  if (!payload?.exp) {
    // If we can't decode or no exp claim, assume it might be expired
    return true;
  }

  const expiresAtMs = payload.exp * 1000; // Convert to milliseconds
  const bufferMs = bufferSeconds * 1000;
  const now = Date.now();

  return now > expiresAtMs - bufferMs;
}

/**
 * Check if token is completely expired (past expiry time)
 * @param token - The JWT token string
 * @returns true if token is past its expiry time
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);

  if (!payload?.exp) {
    return true;
  }

  const expiresAtMs = payload.exp * 1000;
  return Date.now() > expiresAtMs;
}

/**
 * Perform token refresh with singleton pattern to prevent race conditions
 * @returns Promise resolving to true if refresh succeeded, false otherwise
 */
export async function performTokenRefresh(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = executeRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Internal function to execute the actual token refresh
 */
async function executeRefresh(): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { $clientAccount } = useNuxtApp() as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const auth = useAuth() as any;

  // Get refresh token from auth strategy
  const refreshToken = auth.strategy.refreshToken.get();

  if (
    !refreshToken ||
    refreshToken === 'false' ||
    refreshToken === 'undefined'
  ) {
    return false;
  }

  try {
    const response = await $clientAccount.auth.refresh({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: auth.strategy.options.clientId,
    });

    if (response?.data?.access_token) {
      // Update access token
      auth.strategy.token.set('Bearer ' + response.data.access_token);

      // Update refresh token if a new one was provided
      if (response.data.refresh_token) {
        auth.strategy.refreshToken.set(response.data.refresh_token);
      }

      return true;
    }

    return false;
  } catch {
    return false;
  }
}
